import { createRouter, createWebHistory } from 'vue-router'
import { useSimpleAuthStore } from '@/stores/simpleAuth'

function getHomeRoute(role?: string): string {
  switch (role) {
    case 'medecin':    return '/imagerie/medecin'
    case 'technicien': return '/imagerie/technicien'
    case 'caissier':   return '/dashboard'
    default:           return '/imagerie/accueil'
  }
}

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    roles?: string[]
    module?: string
    chefServiceOrAdmin?: boolean
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      redirect: () => {
        const authStore = useSimpleAuthStore()
        return getHomeRoute(authStore.user?.role)
      }
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { requiresAuth: false }
    },
    {
      // Page mobile pour upload de documents via QR code — sans authentification
      path: '/upload/:tokenId',
      name: 'UploadMobile',
      component: () => import('@/views/UploadMobilePage.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/profile',
      name: 'Profile',
      component: () => import('@/views/ProfileView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/unauthorized',
      name: 'Unauthorized',
      component: () => import('@/views/UnauthorizedView.vue')
    },
    {
      path: '/patients',
      name: 'Patients',
      meta: { roles: ['admin', 'medecin', 'assistant', 'caissier'] },
      children: [
        {
          path: '',
          name: 'PatientsList',
          component: () => import('@/views/patients/PatientListView.vue'),
          meta: { requiresAuth: true, module: 'patients' }
        },
        {
          path: 'create',
          name: 'CreatePatient',
          component: () => import('@/views/patients/CreatePatientView.vue'),
          meta: { requiresAuth: true, module: 'patients' }
        },
        {
          path: ':id',
          name: 'PatientDetail',
          component: () => import('@/views/patients/PatientDetailView.vue'),
          meta: { requiresAuth: true, module: 'patients' }
        },
        {
          path: ':id/edit',
          name: 'EditPatient',
          component: () => import('@/views/patients/EditPatientView.vue'),
          meta: { requiresAuth: true, module: 'patients' }
        },
        {
          path: ':id/dossier',
          name: 'PatientDossier',
          component: () => import('@/views/patients/PatientDossierView.vue'),
          meta: { requiresAuth: true, module: 'patients' }
        },
        {
          path: ':id/history',
          name: 'PatientHistory',
          component: () => import('@/views/patients/PatientHistoryView.vue'),
          meta: { requiresAuth: true, module: 'patients' }
        }
      ]
    },
    {
      path: '/medecins',
      name: 'Medecins',
      meta: { roles: ['admin', 'medecin'] },
      children: [
        {
          path: '',
          name: 'MedecinsList',
          component: () => import('@/views/medecins/MedecinListView.vue'),
          meta: { requiresAuth: true, module: 'medecins' }
        },
        {
          path: 'create',
          name: 'CreateMedecin',
          component: () => import('@/views/medecins/CreateMedecinView.vue'),
          meta: { requiresAuth: true, module: 'medecins' }
        },
        {
          path: ':id',
          name: 'MedecinDetail',
          component: () => import('@/views/medecins/MedecinDetailView.vue'),
          meta: { requiresAuth: true, module: 'medecins' }
        },
        {
          path: ':id/planning',
          name: 'MedecinPlanning',
          component: () => import('@/views/medecins/MedecinPlanningView.vue'),
          meta: { requiresAuth: true, module: 'medecins' }
        }
      ]
    },
    {
      path: '/imagerie',
      name: 'Imagerie',
      meta: { roles: ['admin', 'medecin', 'technicien', 'assistant'] },
      children: [
        {
          path: '',
          name: 'ImagerieRoot',
          redirect: { name: 'WorklistJour' },
        },
        {
          path: 'worklist',
          name: 'WorklistJour',
          component: () => import('@/views/imagerie/WorklistJourView.vue'),
          meta: { requiresAuth: true, module: 'imagerie' }
        },
        {
          path: 'accueil',
          name: 'ExamensList',
          component: () => import('@/views/imagerie/ExamensListView.vue'),
          meta: { requiresAuth: true, module: 'imagerie' }
        },
        {
          path: 'rendez-vous',
          name: 'PriseRdv',
          component: () => import('@/views/imagerie/PriseRdvView.vue'),
          meta: { requiresAuth: true, module: 'imagerie' }
        },
        {
          path: 'examens',
          name: 'ExamensTracking',
          component: () => import('@/views/imagerie/ExamensTrackingView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin', 'medecin', 'assistant'] }
        },
        {
          path: 'create',
          name: 'CreateExamen',
          component: () => import('@/views/imagerie/CreateExamenView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin', 'medecin', 'assistant'] }
        },
        {
          path: 'prescription',
          name: 'ExamenPrescription',
          component: () => import('@/views/imagerie/ExamenPrescriptionView.vue'),
          meta: { requiresAuth: true, module: 'imagerie' }
        },
        {
          path: ':id',
          name: 'ExamenDetail',
          component: () => import('@/views/imagerie/ExamenDetailView.vue'),
          meta: { requiresAuth: true, module: 'imagerie' }
        },
        {
          path: ':id/interpretation',
          name: 'InterpretationDetail',
          component: () => import('@/views/imagerie/InterpretationDetailView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin', 'medecin'] }
        },
        {
          path: ':id/interpretation/dictee',
          name: 'InterpretationDictee',
          component: () => import('@/views/imagerie/InterpretationDicteeView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin', 'medecin'] }
        },
        {
          path: ':id/edit',
          name: 'EditExamen',
          component: () => import('@/views/imagerie/EditExamenView.vue'),
          meta: { requiresAuth: true, module: 'imagerie' }
        },
        {
          path: ':id/resultats',
          name: 'ExamenResultats',
          component: () => import('@/views/imagerie/ExamenResultatsView.vue'),
          meta: { requiresAuth: true, module: 'imagerie' }
        },
        {
          path: 'rapports',
          name: 'RapportsComplets',
          component: () => import('@/views/imagerie/components/RapportsCompletsView.vue'),
          meta: { requiresAuth: true, module: 'imagerie' }
        },
        {
          path: 'stats',
          name: 'ImageriStats',
          component: () => import('@/views/imagerie/ImageriStatsView.vue'),
          meta: { requiresAuth: true, module: 'imagerie' }
        },
        {
          path: 'stats/examens',
          name: 'ExamenStats',
          component: () => import('@/views/imagerie/stats/ExamenStatsView.vue'),
          meta: { requiresAuth: true, module: 'imagerie' }
        },
        {
          path: 'stats/performance',
          name: 'PerformanceStats',
          component: () => import('@/views/imagerie/stats/PerformanceStatsView.vue'),
          meta: { requiresAuth: true, module: 'imagerie' }
        },
        {
          path: 'personnel/techniciens',
          name: 'TechniciensListView',
          component: () => import('@/views/imagerie/personnel/TechniciensListView.vue'),
          meta: { requiresAuth: true, module: 'imagerie' }
        },
        {
          path: 'personnel/radiologues',
          name: 'RadiologuesListView',
          component: () => import('@/views/imagerie/personnel/RadiologuesListView.vue'),
          meta: { requiresAuth: true, module: 'imagerie' }
        },
        {
          path: 'personnel/manipulateurs',
          name: 'ManipulateursListView',
          component: () => import('@/views/imagerie/personnel/ManipulateursListView.vue'),
          meta: { requiresAuth: true, module: 'imagerie' }
        },
        {
          path: 'administration',
          name: 'AdministrationImageriView',
          component: () => import('@/views/imagerie/administration/AdministrationImageriView.vue'),
          meta: { requiresAuth: true, module: 'imagerie' }
        },
        {
          path: 'maintenance-salles',
          name: 'MaintenanceSalleView',
          component: () => import('@/views/imagerie/administration/MaintenanceSalleView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin'] }
        },
        {
          path: 'salle-attente',
          name: 'SalleAttenteTech',
          component: () => import('@/views/imagerie/SalleAttenteTechView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin', 'technicien'] }
        },
        {
          path: 'technicien',
          name: 'TechnicienView',
          component: () => import('@/views/imagerie/MonEspaceTechnicienView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin', 'technicien'] }
        },
        {
          path: 'medecin',
          name: 'MonEspaceMedecinView',
          component: () => import('@/views/imagerie/MonEspaceMedecinView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin', 'medecin'] }
        },
        {
          path: 'technicien/:id/poste',
          name: 'TechnicienWorkstation',
          component: () => import('@/views/imagerie/TechnicienWorkstationView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin', 'technicien'] }
        },
        {
          path: 'echographie/:id',
          name: 'EchographieWorkstation',
          component: () => import('@/views/imagerie/EchographieWorkstationView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin', 'medecin', 'assistant'] }
        },
        {
          path: 'radiologue',
          name: 'RadiologueView',
          component: () => import('@/views/imagerie/RadiologueView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin', 'medecin'] }
        },
        {
          path: 'compte-rendu/:id',
          name: 'CompteRenduWorkstation',
          component: () => import('@/views/imagerie/CompteRenduWorkstationView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin', 'medecin', 'assistant'] }
        },
        {
          path: 'modeles-cr',
          name: 'ModelesCR',
          component: () => import('@/views/imagerie/ModelesCRView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin', 'medecin'] }
        },
        {
          path: 'transcription',
          name: 'TranscriptionView',
          component: () => import('@/views/imagerie/TranscriptionView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin', 'assistant', 'medecin'] }
        },
        {
          path: 'transcription/:id',
          name: 'TranscriptionSaisie',
          component: () => import('@/views/imagerie/TranscriptionSaisieView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin', 'assistant', 'medecin'] }
        },
        {
          path: 'echographie-secretaire',
          name: 'SecretaireEchoView',
          component: () => import('@/views/imagerie/SecretaireEchoView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin', 'assistant'] }
        },
        {
          path: 'secretaire-irm',
          name: 'SecretaireIrmView',
          component: () => import('@/views/imagerie/SecretaireIrmView.vue'),
          meta: { requiresAuth: true, module: 'imagerie', roles: ['admin', 'assistant'] }
        }
      ]
    },
    {
      path: '/rendez-vous',
      name: 'RendezVous',
      meta: { roles: ['admin', 'medecin', 'assistant'] },
      children: [
        {
          path: '',
          name: 'RendezVousList',
          component: () => import('@/views/rendez-vous/RendezVousListView.vue'),
          meta: { requiresAuth: true, module: 'rendez-vous' }
        },
        {
          path: 'create',
          name: 'CreateRendezVous',
          component: () => import('@/views/rendez-vous/RendezVousCreateView.vue'),
          meta: { requiresAuth: true, module: 'rendez-vous' }
        },
        {
          path: 'planning',
          name: 'PlanningRendezVous',
          component: () => import('@/views/rendez-vous/PlanningView.vue'),
          meta: { requiresAuth: true, module: 'rendez-vous' }
        }
      ]
    },
    {
      path: '/notifications',
      name: 'Notifications',
      component: () => import('@/views/NotificationsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/transcriptions',
      name: 'Transcriptions',
      children: [
        {
          path: '',
          name: 'TranscriptionsList',
          component: () => import('@/views/transcription/TranscriptionsView.vue'),
          meta: { requiresAuth: true, module: 'transcriptions' }
        },
        {
          path: 'radiologue',
          name: 'RadiologueTranscription',
          component: () => import('@/views/transcription/RadiologueTranscriptionView.vue'),
          meta: { requiresAuth: true, module: 'transcriptions', roles: ['admin', 'medecin'] }
        },
        {
          path: 'assistant',
          name: 'AssistantTranscription',
          component: () => import('@/views/transcription/SecretaireTranscriptionView.vue'),
          meta: { requiresAuth: true, module: 'transcriptions', roles: ['assistant', 'admin'] }
        },
        {
          path: 'secretaire',
          redirect: '/transcriptions/assistant'
        },
        {
          path: ':id',
          name: 'TranscriptionDetail',
          component: () => import('@/views/transcription/TranscriptionDetailView.vue'),
          meta: { requiresAuth: true, module: 'transcriptions' }
        }
      ]
    },
    {
      path: '/administration',
      name: 'Administration',
      meta: { roles: ['admin'] },
      children: [
        {
          path: '',
          name: 'AdminDashboard',
          component: () => import('@/views/administration/AdminDashboardView.vue'),
          meta: { requiresAuth: true, module: 'administration' }
        },
        {
          path: 'users',
          name: 'UserManagement',
          component: () => import('@/views/administration/UserManagementView.vue'),
          meta: { requiresAuth: true, module: 'administration' }
        },
        {
          path: 'stats',
          name: 'Statistics',
          component: () => import('@/views/admin/StatisticsView.vue'),
          meta: { requiresAuth: true, module: 'administration' }
        }
      ]
    },
    {
      path: '/reporting',
      redirect: '/rapport'
    },
    {
      path: '/analyse',
      redirect: '/rapport'
    },
    {
      path: '/rapport',
      name: 'Rapport',
      component: () => import('@/views/analyse/AnalyseView.vue'),
      meta: { requiresAuth: true, chefServiceOrAdmin: true }
    },
    {
      path: '/gestion/tarifs',
      name: 'GestionTarifs',
      component: () => import('@/views/gestion/GestionTarifsView.vue'),
      meta: { requiresAuth: true, chefServiceOrAdmin: true }
    },
    {
      path: '/gestion/stats',
      name: 'StatistiquesGestion',
      component: () => import('@/views/gestion/StatistiquesGestionView.vue'),
      meta: { requiresAuth: true, chefServiceOrAdmin: true }
    },
    {
      path: '/gestion/paiements',
      name: 'PaiementRadiologue',
      component: () => import('@/views/gestion/PaiementRadiologue.vue'),
      meta: { requiresAuth: true, chefServiceOrAdmin: true }
    },
    {
      path: '/documents',
      name: 'Documents',
      component: () => import('@/views/documents/DocumentsView.vue'),
      meta: { requiresAuth: true, module: 'documents' }
    },
    {
      path: '/calendrier',
      name: 'Calendrier',
      component: () => import('@/views/CalendrierView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/test',
      name: 'Test',
      children: [
        {
          path: '',
          name: 'TestIndex',
          component: () => import('@/views/test/TestIndexView.vue'),
          meta: { requiresAuth: false }
        },
        {
          path: 'connection',
          name: 'ConnectionTest',
          component: () => import('@/views/test/ConnectionTestView.vue'),
          meta: { requiresAuth: false }
        },
        {
          path: 'login',
          name: 'LoginTest',
          component: () => import('@/views/test/LoginTestView.vue'),
          meta: { requiresAuth: false }
        },
        {
          path: 'integration',
          name: 'IntegrationTest',
          component: () => import('@/views/test/IntegrationTestView.vue'),
          meta: { requiresAuth: false }
        },
        {
          path: 'notifications',
          name: 'NotificationTest',
          component: () => import('@/views/test/NotificationTestView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'assistant',
          name: 'AssistantTest',
          component: () => import('@/views/test/SecretaireTestView.vue'),
          meta: { requiresAuth: false }
        }
      ]
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('@/views/NotFoundView.vue')
    }
  ]
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useSimpleAuthStore()

  // V├®rifier si la route n├®cessite une authentification
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/login')
    return
  }

  // Rediriger si connecté et tentative d'accès à login
  if (to.name === 'Login' && authStore.isAuthenticated) {
    next(getHomeRoute(authStore.user?.role))
    return
  }

  // V├®rifier les r├┤les autoris├®s ÔÇö on cherche la route la plus sp├®cifique
  // qui d├®finit des r├┤les (enfant en premier, puis parents)
  if (authStore.isAuthenticated) {
    const restrictedRoute = [...to.matched]
      .reverse()
      .find(r => r.meta.roles && Array.isArray(r.meta.roles))

    if (restrictedRoute) {
      const userRole = authStore.user?.role
      if (!userRole || !(restrictedRoute.meta.roles as string[]).includes(userRole)) {
        next('/unauthorized')
        return
      }
    }

    // Vérifier l'accès admin ou chef de service
    const chefRoute = [...to.matched].find(r => r.meta.chefServiceOrAdmin)
    if (chefRoute && !authStore.isAdmin && !authStore.isChefService) {
      next('/unauthorized')
      return
    }
  }

  next()
})

export default router